import type { Action } from "../actions.types";
import { isOrgAdminAction } from "./isOrgAdminAction";
import { isActionVisibleOnDashboard } from "./isActionVisibleOnDashboard";
import { sortActionsByStatusAndDueDate } from "./sortActionsByStatusAndDueDate";

export type OrganizationActions = {
  organizationId: number;
  organizationName: string;
  actions: Action[];
};

/**
 * Groups org admin actions by organization.
 * Filters to only visible org admin actions, then groups by organization ID.
 *
 * @param actions - Array of user actions
 * @returns Array of organization action groups, each containing the org's actions
 */
export function groupActionsByOrganization(
  actions: Action[]
): OrganizationActions[] {
  if (!actions || actions.length === 0) return [];

  const orgAdminActions = actions.filter(
    (action) => isOrgAdminAction(action) && isActionVisibleOnDashboard(action)
  );

  if (orgAdminActions.length === 0) return [];

  const groupedByOrg = new Map<number, OrganizationActions>();

  for (const action of orgAdminActions) {
    const targetData = action.target_object_data as {
      id?: number;
      name?: string;
    } | null;
    const bindingData = action.binding_object_data as {
      id?: number;
      name?: string;
    } | null;

    const orgId = targetData?.id ?? bindingData?.id;
    const orgName =
      targetData?.name ?? bindingData?.name ?? "Unknown Organization";

    if (!orgId) continue;

    if (!groupedByOrg.has(orgId)) {
      groupedByOrg.set(orgId, {
        organizationId: orgId,
        organizationName: orgName,
        actions: [],
      });
    }

    groupedByOrg.get(orgId)!.actions.push(action);
  }

  // Sort actions within each organization group by status and due date
  const result = Array.from(groupedByOrg.values());
  for (const group of result) {
    group.actions = sortActionsByStatusAndDueDate(group.actions);
  }
  return result;
}
